import { observable, action, computed, autorun } from 'mobx';
import XoApi from '~/glm/utils/xo-api';
import WeddingApi from '~/glm/utils/wedding-api';
import bindAll from 'lodash/bindAll';
import moment from 'moment';
import setCookie from '~/glm/utils/sessions';
import Store from '~/glm/models/store';
import ErrorHandler from '~/glm/utils/error-handler';

class UserStore extends Store {
  @observable memberId = '';
  @observable legacyUserId = '';
  @observable firstName = '';
  @observable lastName = '';
  @observable role = 'bride';
  @observable email;
  @observable password;
  @observable fianceFirstName = '';
  @observable fianceLastName = '';
  @observable fianceRole = 'groom';
  @observable weddingDate;
  @observable weddingLocation;
  @observable wedding;
  @observable dismissedWwsNotPublishedAlert = false;
  @observable dismissedSpellingAlert = false;
  @observable dismissedEnableRSVPAlert = false;
  @observable isReady = false;
  @observable isLoggedIn = false;
  @observable hideDate = false;
  @observable guestRange = '';
  @observable weddingApiObj;

  static coupleRoles = ['bride', 'groom', 'other'];

  constructor() {
    super();

    // this will cause the legacy knot header to show the logged-in menu
    autorun(() => {
      if (this.isLoggedIn && document) document.body.classList.add('logged-in');
    });

    bindAll(this, [
      'initBasicInfo',
      'getBasicInfo',
      'setBasicInfo',
      'initWedding',
      'getWedding',
      'setWedding',
      'updateGuestRange',
      'setProp',
    ]);
  }

  @action
  initBasicInfo(callCount) {
    return this.getBasicInfo()
      .then(() => {
        // If basic info doesn't return a wedding,
        // retry until it does
        if (!this.wedding) {
          if (callCount > 9) {
            ErrorHandler.notify({
              name: 'no wedding returned from basic_info after 10 attempts!',
              context: { member_id: this.memberId },
            });
            return;
          }
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              if (callCount) {
                callCount++;
              } else {
                callCount = 1;
              }
              this.initBasicInfo(callCount)
                .then(resolve)
                .catch(reject);
            }, 1500);
          });
        }
        // this.setProp('isReady', true);
        this.setProp('isLoggedIn', true);
      })
      .catch(() => this.setProp('isReady', true));
  }

  @action
  initWedding(memberId, callCount) {
    return this.getWedding(memberId)
      .then(() => {
        // If basic info doesn't return a wedding,
        // retry until it does
        if (!this.wedding) {
          if (callCount > 9) {
            ErrorHandler.notify({
              name: 'no wedding returned from basic_info after 10 attempts!',
              context: { member_id: this.memberId },
            });
            return;
          }
          return new Promise((resolve, reject) => {
            setTimeout(() => {
              if (callCount) {
                callCount++;
              } else {
                callCount = 1;
              }
              this.initWedding(memberId, callCount)
                .then(resolve)
                .catch(reject);
            }, 1500);
          });
        }
        this.setProp('isReady', true);
        this.setProp('isLoggedIn', true);
      })
      .catch(() => this.setProp('isReady', true));
  }

  getBasicInfo() {
    return XoApi.get('/basic_info').then(this.setBasicInfo);
  }

  getWedding(memberId) {
    return WeddingApi.get(`/members/${memberId}/wedding`).then(this.setWedding);
  }

  updateGuestRange(newGuestRange) {
    const data = {
      wedding: {
        ...this.weddingApiObj,
        guest_range: newGuestRange,
      },
    };
    return WeddingApi.put('/weddings', data).then(this.setWedding);
  }

  @action
  setBasicInfo(basicInfoJSON) {
    const { wedding, member } = basicInfoJSON;
    this.wedding = wedding;
    this.memberId = member.id;
    this.legacyUserId = member.legacyUserId;
    this.firstName = member.firstName;
    this.lastName = member.lastName;
    this.role = member.role;
    this.fianceFirstName = member.fianceFirstName;
    this.fianceLastName = member.fianceLastName;
    this.fianceRole = member.fianceRole;
    this.hideDate = wedding.hideDate;

    this.initWedding(member.id);

    const date = moment(member.weddingDate);
    this.weddingDate = date.format('YYYY-MM-DD');
    this.weddingLocation = member.weddingLocation;
  }

  @action
  setWedding(weddingJSON) {
    const { wedding } = weddingJSON;
    this.weddingApiObj = { ...wedding };
    this.guestRange = wedding.guest_range || '';
    return weddingJSON;
  }

  @computed
  get hasWWS() {
    return !(this.wedding.state === 'uninitialized' || this.wedding.guestListOnly);
  }

  @computed
  get isWwsPublished() {
    return this.wedding.state === 'published';
  }

  @computed
  get needsWwsNotPublishedAlert() {
    return (
      !this.isWwsPublished &&
      !this.dismissedWwsNotPublishedAlert &&
      !localStorage.getItem('xo_dismissedWwsNotPublished')
    );
  }

  getNeedsRSVPEnabledAlert(eventId) {
    return (
      this.hasWWS && this.getPrivateRsvp === 'secure' && !localStorage.getItem(`xo_dismissEnableRSVPAlert${eventId}`)
    );
  }

  @action
  setDismissRSVPEnabledAlert(eventId) {
    this.dismissEnableRSVPAlert = true;
    localStorage.setItem(`xo_dismissEnableRSVPAlert${eventId}`, true);
  }

  @action
  setDismissedWwsNotPublishedAlert() {
    this.dismissedWwsNotPublishedAlert = true;
    localStorage.setItem('xo_dismissedWwsNotPublished', true);
  }

  @computed
  get needsPublicAlert() {
    return !this.wedding.privateRsvp;
  }

  @computed
  get needsSpellingAlert() {
    return this.wedding.privateRsvp && !this.dismissedSpellingAlert && !localStorage.getItem('xo_dismissedSpelling');
  }

  @action
  setDismissedSpellingAlert() {
    this.dismissedSpellingAlert = true;
    localStorage.setItem('xo_dismissedSpelling', true);
  }

  @action
  resetWarnings() {
    this.dismissedSpellingAlert = false;
    this.dismissedWwsNotPublishedAlert = false;
    Object.keys(localStorage)
      .filter(key => key.includes('xo_dismissed'))
      .forEach(key => localStorage.removeItem(key));
  }

  @action
  setPrivateRsvp(setting) {
    this.wedding.privateRsvp = setting;
    return XoApi.put(`/v2/member_weddings/${this.wedding.id}`, {
      privateRsvp: setting,
    });
  }

  @computed
  get getPrivateRsvp() {
    return this.wedding.privateRsvp ? 'secure' : 'public';
  }

  @action
  recordLogin(sessionToken) {
    // Setting to 365 because that's what GS was setting it to
    setCookie('xo-session-token', sessionToken, 365);
    this.isLoggedIn = true;
  }

  @action
  updateHideDate(hideDate) {
    this.hideDate = hideDate;
  }
}

export default UserStore;
